import { fOrdinal } from '@aibees/shared/utils';
import { find, lowerCase, map } from 'lodash-es';
import { NewStateEntry, TemplateCategory, TransferredData } from '../types';

export const updateDraftItemsBasedOnEditValue = (
  transferredDataArray: TransferredData[],
  defaultItemsArray: TemplateCategory[]
): TemplateCategory[] => {
  return map(defaultItemsArray, (defaultCategory) => {
    // Find corresponding category in transferredData
    const transferredCategory = find(
      transferredDataArray,
      (td) => lowerCase(td.categoryName) === lowerCase(defaultCategory.name)
    );

    if (!transferredCategory) {
      // No transferred data for this category, return default with all unchecked
      return {
        ...defaultCategory,
        entries: map(defaultCategory.entries, (entry) => ({
          ...entry,
          checked: false,
          fields: map(entry.fields, (field) => ({
            ...field,
            selected: false,
          })),
        })),
      };
    }

    // Get the first entry and bottom entry from defaultCategory as templates
    const firstDefaultEntry = defaultCategory.entries[0];
    const bottomDefaultEntry = defaultCategory.entries.find(
      (entry) => entry.isBottom
    );

    // Sort transferred entries: regular entries first (by index), then bottom entries
    const sortedTransferredEntries = [
      ...transferredCategory.entries.filter((e) => !e.isBottom),
      ...transferredCategory.entries.filter((e) => e.isBottom),
    ].sort((a, b) => {
      if (a.isBottom && !b.isBottom) return 1;
      if (!a.isBottom && b.isBottom) return -1;
      return (a.index || 0) - (b.index || 0);
    });

    // Reconstruct entries from transferredData
    const reconstructedEntries = map(
      sortedTransferredEntries,
      (transferredEntry) => {
        const entryWithExtras = transferredEntry as NewStateEntry & {
          name?: string;
          checked?: boolean;
        };

        // For bottom entries, use bottom entry from defaultCategory as template
        if (transferredEntry.isBottom && bottomDefaultEntry) {
          return {
            ...bottomDefaultEntry,
            checked: entryWithExtras.checked ?? false,
            notAdded: transferredEntry.notAdded ?? true,
            isBottom: true,
            fields: map(bottomDefaultEntry.fields, (field) => ({
              ...field,
              selected: transferredEntry.fields.includes(field.type),
            })),
          };
        }

        // For regular entries, find matching entry in defaultCategory by name or index
        // If not found, clone from first entry
        let templateEntry = defaultCategory.entries.find(
          (entry) =>
            !entry.isBottom &&
            (entry.name === entryWithExtras.name ||
              entry.name ===
                `${fOrdinal((transferredEntry.index || 0) + 1)} Entry`)
        );

        // If not found, use first entry as template (for new entries like "2nd Entry")
        if (!templateEntry && firstDefaultEntry) {
          templateEntry = firstDefaultEntry;
        }

        if (!templateEntry) {
          // Fallback: create a basic entry structure
          return {
            name:
              entryWithExtras.name ||
              `${fOrdinal((transferredEntry.index || 0) + 1)} Entry`,
            checked: entryWithExtras.checked ?? true,
            notAdded: transferredEntry.notAdded ?? false,
            isBottom: false,
            fields: [],
          };
        }

        // Build the entry based on template
        return {
          ...templateEntry,
          name: entryWithExtras.name || templateEntry.name,
          checked: entryWithExtras.checked ?? true,
          notAdded: transferredEntry.notAdded ?? false,
          isBottom: false,
          fields: map(templateEntry.fields, (field) => ({
            ...field,
            selected: transferredEntry.fields.includes(field.type),
          })),
        };
      }
    );

    return {
      ...defaultCategory,
      entries: reconstructedEntries,
    };
  });
};
